import React, { useState, useEffect } from 'react';
import styles from './styles.css';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { CountryMultiSelect } from '../';
import { useCustomGroups } from '../../hooks';
import { expandLocationsToCountries } from './utils';

function CloseIcon({ className }) {
	return (
		<svg
			className={className}
			width="20"
			height="20"
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
		</svg>
	);
}

function TrashIcon({ className }) {
	return (
		<svg
			className={className}
			width="18"
			height="18"
			viewBox="14 14 20 20"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M18.0977 34C17.5798 34 17.1391 33.82 16.7757 33.4599C16.4123 33.0998 16.2306 32.6631 16.2306 32.1499V16.7817H15V14.9315H20.4147V14H27.8396V14.9315H33.2609V16.7817H32.0302V32.1499C32.0302 32.6495 31.8451 33.0828 31.4749 33.4497C31.1047 33.8166 30.6674 34 30.1632 34H18.0977ZM30.1632 16.7817H18.0977V32.1499H30.1632V16.7817ZM20.9597 29.8665H22.7092V19.0314H20.9597V29.8665ZM25.5516 29.8665H27.3077V19.0314H25.5516V29.8665Z"
				fill="currentColor"
			/>
		</svg>
	);
}

function PencilIcon({ className }) {
	return (
		<svg
			className={className}
			xmlns="http://www.w3.org/2000/svg"
			width="16"
			height="16"
			fill="currentColor"
			viewBox="0 0 16 16"
		>
			<path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325" />
		</svg>
	);
}

export default function CustomGroupsManager({
	countryList = [],
	selectedLocations = [],
	onGroupSelect,
	onGroupUpdate,
	className = '',
}) {
	const { t } = useTranslation();
	const {
		customGroups,
		createCustomGroup,
		updateCustomGroup,
		deleteCustomGroup,
		generateCustomGroupUrlParam,
		parseCustomGroupUrlParam,
		validateCustomGroupName,
		MAX_CUSTOM_GROUP_NAME_LENGTH,
		customGroupsOperationInProgress,
		customGroupsError,
	} = useCustomGroups();

	const [showCreateModal, setShowCreateModal] = useState(false);
	const [editingGroup, setEditingGroup] = useState(null);
	const [newGroupName, setNewGroupName] = useState('');
	const [selectedCountriesForGroup, setSelectedCountriesForGroup] = useState([]);
	const [validationError, setValidationError] = useState(null);
	const [groupPendingDeletion, setGroupPendingDeletion] = useState(null);
	const [deleteError, setDeleteError] = useState(null);

	const handleCreateGroup = () => {
		setEditingGroup(null);
		setNewGroupName('');
		setValidationError(null);

		// Expand all selected locations (countries, custom groups, standard groups) into country ISOs
		const uniqueCountries = expandLocationsToCountries(selectedLocations, countryList, parseCustomGroupUrlParam);
		setSelectedCountriesForGroup(uniqueCountries);

		setShowCreateModal(true);
	};

	const handleEditGroup = (groupId) => {
		const group = customGroups[groupId];
		setEditingGroup(groupId);
		setNewGroupName(group.name);
		setSelectedCountriesForGroup(group.countries);
		setValidationError(null);
		setShowCreateModal(true);
	};

	const handleSaveGroup = async () => {
		const nameValidation = validateCustomGroupName(newGroupName);
		if (!nameValidation.isValid) {
			setValidationError(t(nameValidation.error));
			return;
		}

		if (selectedCountriesForGroup.length < 2) {
			setValidationError(t('A custom group must contain at least 2 countries.'));
			return;
		}

		try {
			let group;
			if (editingGroup) {
				group = await updateCustomGroup(editingGroup, newGroupName, selectedCountriesForGroup);
			} else {
				group = await createCustomGroup(newGroupName, selectedCountriesForGroup);
			}

			setShowCreateModal(false);
			setValidationError(null);

			// Notify parent if group was updated and is currently selected
			if (editingGroup && onGroupUpdate && group) {
				const urlParam = generateCustomGroupUrlParam(group.id);
				const isCurrentlySelected = selectedLocations.includes(urlParam);

				if (isCurrentlySelected) {
					onGroupUpdate(editingGroup, group.id, group.name, group.countries);
				}
			}
		} catch (error) {
			setValidationError(t(error.message));
		}
	};

	const handleDeleteGroup = (groupId) => {
		const group = customGroups[groupId];
		if (!group) {
			return;
		}

		setDeleteError(null);
		setGroupPendingDeletion(groupId);
	};

	const closeDeleteModal = () => {
		setGroupPendingDeletion(null);
		setDeleteError(null);
	};

	const confirmDeleteGroup = async () => {
		if (!groupPendingDeletion) {
			return;
		}

		const group = customGroups[groupPendingDeletion];
		if (!group) {
			closeDeleteModal();
			return;
		}

		try {
			await deleteCustomGroup(groupPendingDeletion);
			closeDeleteModal();
		} catch (error) {
			setDeleteError(error.message);
		}
	};

	const handleSelectGroup = (groupId) => {
		const customGroupParam = generateCustomGroupUrlParam(groupId);

		// Check if already selected
		const isAlreadySelected = selectedLocations.includes(customGroupParam);

		if (onGroupSelect) {
			onGroupSelect(customGroupParam, !isAlreadySelected);
		}
	};

	const getCountryDisplayName = (iso) => {
		const country = countryList?.find((c) => c.iso === iso);
		return country ? country.fullName : iso;
	};

	// Determine if any selected locations resolve to countries (directly or via groups)
	const hasSelectedCountries = selectedLocations?.some((loc) => {
		// Direct country ISO code
		if (/^[A-Z]{2,3}$/.test(loc)) return true;
		// Custom group with countries
		const cg = parseCustomGroupUrlParam(loc);
		if (cg && cg.countries && cg.countries.length > 0) return true;
		// Standard group containing countries
		if (countryList.some((c) => c.grps && c.grps.includes(loc))) return true;
		return false;
	});

	const renderGroupFlagPreview = (groupId) => {
		const group = customGroups[groupId];
		if (!group || !group.countries || group.countries.length === 0) {
			return null;
		}

		const firstThreeCountries = group.countries.slice(0, 3);

		return (
			<div className={styles.groupFlagPreview}>
				{firstThreeCountries.map((iso, index) => (
					<img
						key={iso}
						className={styles.previewFlag}
						src={`/img/roundflags/${iso.toLowerCase()}.svg`}
						alt={getCountryDisplayName(iso)}
						style={{ zIndex: 3 - index }}
					/>
				))}
				{group.countries.length > 3 && (
					<span className={styles.moreCountriesIndicator}>+{group.countries.length - 3}</span>
				)}
			</div>
		);
	};

	const pendingDeletionGroup = groupPendingDeletion ? customGroups[groupPendingDeletion] : null;

	return (
		<div className={`${styles.customGroupsContainer} ${className}`}>
			<div className={styles.customGroupsSection}>
				<div className={styles.customGroupsTitleRow}>
					<div className={styles.customGroupsTitle}>{t('Custom groups')}</div>
					<span
						className={styles.infoTooltip}
						data-tooltip-id="custom-groups-tooltip"
						data-tooltip-content={t(
							'Custom groups let you create your own collection of countries for easy comparison.',
						)}
						aria-label={t('What are custom groups?')}
					>
						<img src="/dist/src/img/icons/infoSymbol.png" alt="" className={styles.infoIcon} />
					</span>
				</div>
				<button
					type="button"
					className={styles.addCustomGroupButton}
					onClick={handleCreateGroup}
					aria-label={hasSelectedCountries ? t('Create group from selection') : t('Add new group')}
				>
					{hasSelectedCountries ? t('Create group from selection') : t('Add new group')}
				</button>
				{Object.keys(customGroups).length > 0 && (
					<ul className={styles.customGroupsList}>
						{Object.keys(customGroups).map((groupId) => {
							const group = customGroups[groupId];
							const isSelected = selectedLocations.some((loc) => parseCustomGroupUrlParam(loc)?.id === groupId);
							return (
								<li key={groupId} className={styles.customGroupItem}>
									<button
										type="button"
										className={`${styles.customGroupName} ${isSelected ? styles.selected : ''}`}
										onClick={() => handleSelectGroup(groupId)}
										title={`Select ${group.name}: ${group.countries?.map((iso) => getCountryDisplayName(iso)).join(', ') || ''}`}
									>
										{renderGroupFlagPreview(groupId)}
										<span className={styles.groupNameText}>{group.name}</span>
									</button>
									<div className={styles.customGroupActions}>
										<button
											type="button"
											className={styles.customGroupEdit}
											onClick={() => handleEditGroup(groupId)}
											title={t('Edit group')}
										>
											<PencilIcon className={styles.editIcon} />
										</button>
										<button
											type="button"
											className={styles.customGroupDelete}
											onClick={() => handleDeleteGroup(groupId)}
											title={t('Delete group')}
										>
											<TrashIcon className={styles.deleteIcon} />
										</button>
									</div>
								</li>
							);
						})}
					</ul>
				)}
			</div>

			{showCreateModal && (
				<div className={styles.modal}>
					<div className={styles.modalContent}>
						<div className={styles.modalHeader}>
							<h3>{editingGroup ? t('Edit group') : t('Enter group name')}</h3>
							<button
								type="button"
								className={styles.closeButton}
								onClick={() => setShowCreateModal(false)}
								aria-label={t('Close')}
							>
								<CloseIcon className={styles.closeIcon} />
							</button>
						</div>
						<div className={styles.formGroup}>
							<label htmlFor="groupName">{t('Group name')}</label>
							<input
								id="groupName"
								type="text"
								value={newGroupName}
								onChange={(e) => {
									setNewGroupName(e.target.value);
									setValidationError(null);
								}}
								placeholder={t('My favourite countries')}
								maxLength={MAX_CUSTOM_GROUP_NAME_LENGTH}
								className={styles.input}
							/>
							{validationError && <div className={styles.errorMessage}>{validationError}</div>}
						</div>
						<div className={styles.formGroup}>
							<label>{t('Select countries')}</label>
							<CountryMultiSelect
								countryList={countryList}
								selectedCountries={selectedCountriesForGroup}
								onChange={setSelectedCountriesForGroup}
								placeholder={t('Search and select countries')}
							/>
							<div className={styles.selectedCountriesInfo}>
								{selectedCountriesForGroup.length === 0 ? (
									<span className={styles.noSelection}>{t('No countries selected')}</span>
								) : (
									<span className={styles.countryCount}>
										{t('{{count}} countries selected', { count: selectedCountriesForGroup.length })}
									</span>
								)}
							</div>
						</div>
						<div className={styles.modalActions}>
							<button type="button" className={styles.cancelButton} onClick={() => setShowCreateModal(false)}>
								{t('Cancel')}
							</button>
							<button
								type="button"
								className={styles.saveButton}
								onClick={handleSaveGroup}
								disabled={customGroupsOperationInProgress}
							>
								{customGroupsOperationInProgress ? t('Saving...') : t('Save')}
							</button>
						</div>
					</div>
				</div>
			)}

			{groupPendingDeletion && pendingDeletionGroup && (
				<div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="deleteGroupTitle">
					<div className={styles.modalContent}>
						<div className={styles.modalHeader}>
							<h3 id="deleteGroupTitle">{t('Delete group')}</h3>
							<button type="button" className={styles.closeButton} onClick={closeDeleteModal} aria-label={t('Close')}>
								<CloseIcon className={styles.closeIcon} />
							</button>
						</div>
						<div className={styles.confirmBody}>
							<p>
								{t('Are you sure you want to delete the group "{{groupName}}"?', {
									groupName: pendingDeletionGroup.name,
								})}
							</p>

							{deleteError && <div className={styles.errorMessage}>{deleteError}</div>}
						</div>
						<div className={styles.modalActions}>
							<button type="button" className={styles.cancelButton} onClick={closeDeleteModal}>
								{t('Cancel')}
							</button>
							<button
								type="button"
								className={styles.deleteButton}
								onClick={confirmDeleteGroup}
								disabled={customGroupsOperationInProgress}
							>
								{t('Delete group')}
							</button>
						</div>
					</div>
				</div>
			)}

			<Tooltip id="custom-groups-tooltip" />
		</div>
	);
}
